import * as React from "react";

import {
  AIAgentThreadContext,
  INERT_AI_AGENT_THREAD_CONTEXT_VALUE,
} from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";

/**
 * Provides a minimal, read-only AIAgentThreadContext for rendering AgentThreadByIdView
 * outside of the normal thread sidebar (e.g. in admin views).
 *
 * The value is `INERT_AI_AGENT_THREAD_CONTEXT_VALUE` — the no-op defaults
 * co-located with the context interface — plus the handful of admin-only
 * overrides below. Most mutation handlers are therefore no-ops and interactive
 * features are disabled. ``navigateToThread`` is the one behavioral
 * exception: ``ForkAgentThreadButton`` calls it on a successful fork to
 * navigate the viewer to the new thread, so the parent can pass
 * ``onAgentThreadClicked`` to receive that signal.
 */
interface ReadOnlyAIAgentThreadProviderProps {
  children: React.ReactNode;
  /** Called when a child requests navigation to a different thread (today,
   *  only ``ForkAgentThreadButton`` after a successful fork). When omitted the
   *  request is dropped silently. */
  onAgentThreadClicked?: (threadId: string) => void;
  /** Forwarded into context so message rows can render an admin-only debug
   *  control under each message. */
  renderMessageDebugControl?: (messageSequenceNum: number) => React.ReactNode;
  /** Forwarded into context so user-message rows can render an admin-only
   *  knowledge-graph control. */
  renderMessageKnowledgeGraphControl?: (
    messageSequenceNum: number,
  ) => React.ReactNode;
  /** Forwarded into context so AgentThreadByIdView can auto-expand the
   *  concluded-task section a scroll target lands in. */
  scrollToMessageSequenceNum?: number;
  /** Forwarded into context as the caption on the highlighted scroll target. */
  scrollTargetLabel?: React.ReactNode;
}

export const ReadOnlyAIAgentThreadProvider: React.FC<
  ReadOnlyAIAgentThreadProviderProps
> = ({
  children,
  onAgentThreadClicked,
  renderMessageDebugControl,
  renderMessageKnowledgeGraphControl,
  scrollToMessageSequenceNum,
  scrollTargetLabel,
}) => {
  const navigateToThread = React.useCallback(
    (threadId: string | undefined) => {
      // A read-only surface has no address for "a new, unsaved thread" — you
      // cannot start one from a conversation you may only look at. Only a
      // request for a real thread (today, a fork) means anything here.
      if (threadId === undefined) {
        return;
      }
      onAgentThreadClicked?.(threadId);
    },
    [onAgentThreadClicked],
  );

  const value = React.useMemo(
    () => ({
      ...INERT_AI_AGENT_THREAD_CONTEXT_VALUE,
      navigateToThread,
      renderMessageDebugControl,
      renderMessageKnowledgeGraphControl,
      scrollToMessageSequenceNum,
      scrollTargetLabel,
    }),
    [
      navigateToThread,
      renderMessageDebugControl,
      renderMessageKnowledgeGraphControl,
      scrollToMessageSequenceNum,
      scrollTargetLabel,
    ],
  );

  return (
    <AIAgentThreadContext.Provider value={value}>
      {children}
    </AIAgentThreadContext.Provider>
  );
};
